import { useCallback, useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import { createPortal } from "react-dom";

/**
 * ============================================================================
 * The certificate lightbox — extracted 2026-09-03 (D-105).
 * ============================================================================
 *
 * IT LIVED INSIDE `CertificateShowcase` UNTIL THE THIRD CONSUMER APPEARED. The
 * award cards on `/achievements` now show certificates too, and D-102 is this
 * repository's record of what a second copy of a layout costs: `/dashboard` and
 * the Home strip drew the same record with different fields, and neither was
 * wrong — they had simply diverged on the first edit that touched one of them.
 * A modal is worse than a layout to copy, because what it holds is not
 * arrangement but a set of keyboard invariants that are individually easy to
 * forget and collectively the difference between a dialog and a trap.
 *
 * WHAT IT CARRIES, AND WHY EACH ONE IS HERE:
 *
 *   · **Escape closes.** Table stakes, and D-089 records a probe that reported
 *     all 90 Escape checks failing on a modal that closes correctly — the
 *     dialog stays mounted through `AnimatePresence`'s exit, so a probe reading
 *     at 120 ms sees it. Uniform failure across every width is the signature of
 *     a broken probe, not a defect.
 *   · **Tab is trapped**, queried LIVE rather than captured on open, so a
 *     re-render inside the dialog cannot leave the trap pointing at a detached
 *     node.
 *   · **Focus returns to the trigger on close.** Without it the next Tab starts
 *     at the top of the document with nothing on screen explaining why — the
 *     D-068 finding about the mobile menu, in a different control.
 *   · **A PORTAL INTO `document.body`, AND WITHOUT IT THIS IS NOT A MODAL.**
 *     `.card-face` carries `backdrop-filter: blur(12px)`, and a backdrop-filter
 *     makes an element a CONTAINING BLOCK for its `position: fixed`
 *     descendants. Measured at 390 before the portal existed: the backdrop was
 *     [18, -152, 350x1205] against a 390x900 viewport — dimming a strip of one
 *     card, starting 152px above the top of the screen, while the rest of the
 *     page stayed bright and clickable. It looked correct in the source and was
 *     wrong in every theme at every width.
 *   · **The image is bounded by HEIGHT as well as width** (D-080). `w-full`
 *     alone let the height fall where it liked: a 1079x1525 credential rendered
 *     704x995 in a 900px viewport and ran 171px below the fold. `100vh` is a
 *     definite bound, which is the half of D-083 that matters here — a
 *     percentage against an indefinite parent is `none`, not a bound.
 *
 * THE TRIGGER IS PASSED IN AS CHILDREN, because the two consumers frame it
 * differently and that is the genuinely surface-specific part: the showcase
 * makes the whole certificate the button, the award card makes a small
 * thumbnail. Everything else is identical by construction — which is a claim
 * about the source, not about the page, so the gate still checks both (D-102).
 */
interface CertificateLightboxProps {
	/** The image to enlarge. */
	src: string;
	/** Alt text, from the content layer. Used on both the trigger and the dialog. */
	alt: string;
	/** The dialog's accessible name and its visible heading. */
	title: string;
	/** The trigger button's accessible name — it says what enlarging will show. */
	triggerLabel: string;
	/** Classes for the trigger button itself. Geometry and focus ring only. */
	triggerClassName: string;
	/** What the trigger looks like. */
	children: ReactNode;
}

export default function CertificateLightbox({
	src,
	alt,
	title,
	triggerLabel,
	triggerClassName,
	children
}: CertificateLightboxProps): JSX.Element {
	const [open, setOpen] = useState(false);
	const triggerRef = useRef<HTMLButtonElement>(null);
	const dialogRef = useRef<HTMLDivElement>(null);

	const close = useCallback((): void => {
		setOpen(false);
		triggerRef.current?.focus();
	}, []);

	useEffect(() => {
		if (!open) return undefined;

		const onKeyDown = (event: KeyboardEvent): void => {
			if (event.key === "Escape") {
				close();
				return;
			}
			if (event.key !== "Tab") return;

			const focusable = dialogRef.current?.querySelectorAll<HTMLElement>(
				"a[href], button:not([disabled]), [tabindex]:not([tabindex='-1'])"
			);
			if (!focusable || focusable.length === 0) return;
			const first = focusable[0];
			const last = focusable[focusable.length - 1];
			if (event.shiftKey && document.activeElement === first) {
				event.preventDefault();
				last.focus();
			} else if (!event.shiftKey && document.activeElement === last) {
				event.preventDefault();
				first.focus();
			}
		};

		document.addEventListener("keydown", onKeyDown);
		dialogRef.current?.focus();
		return () => document.removeEventListener("keydown", onKeyDown);
	}, [open, close]);

	return (
		<>
			<button
				ref={triggerRef}
				type="button"
				onClick={() => setOpen(true)}
				aria-label={triggerLabel}
				className={triggerClassName}
			>
				{children}
			</button>

			{/* Only ever evaluated when `open` is true, and `open` is false on the
			    server and on the first client render — so `document` is never
			    touched during the pre-render (D-042). */}
			{open &&
				createPortal(
					<div
						className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
						role="presentation"
						onClick={close}
					>
						<div
							ref={dialogRef}
							role="dialog"
							aria-modal="true"
							aria-label={title}
							tabIndex={-1}
							className="max-h-full w-full max-w-3xl overflow-auto rounded-[14px] bg-[var(--surface-modal)] p-4 focus:outline-none"
							onClick={(event: React.MouseEvent<HTMLDivElement>) => event.stopPropagation()}
						>
							<div className="flex items-start justify-between gap-4">
								<h4 className="text-sm font-semibold text-[var(--text-strong)]">{title}</h4>
								{/* 44px minimum: below it, on a phone, this is the
								    difference between closing the dialog and reopening
								    it (D-080). */}
								<button
									type="button"
									onClick={close}
									aria-label="Close the certificate preview"
									className="inline-flex min-h-[44px] shrink-0 items-center rounded-[8px] px-3 py-2 text-sm text-[var(--text-primary)] hover:text-[var(--accent-link-hover)] focus:outline-none focus-visible:ring-4 focus-visible:ring-[var(--ring-accent)]"
								>
									Close
								</button>
							</div>
							<img
								src={src}
								alt={alt}
								className="mx-auto mt-4 block max-h-[calc(100vh-9rem)] w-auto max-w-full rounded-[10px] object-contain"
							/>
						</div>
					</div>,
					document.body
				)}
		</>
	);
}
